import { useCallback, useEffect, useState } from 'react';
import type { SorterResult } from 'antd/es/table/interface';
import type { SortOrder as MetricSortOrder } from '../../types';

interface UseMetricSortResult {
    sortBy: string;
    sortOrder: MetricSortOrder;
    handleTableChange: (pagination: unknown, filters: unknown, sorter: SorterResult<any> | SorterResult<any>[]) => void;
    getColumnSortOrder: (dataIndex: string) => 'ascend' | 'descend' | null;
    resetSort: () => void;
}

export function useMetricSort(initialSortBy = ''): UseMetricSortResult {
    const [sortBy, setSortBy] = useState<string>(initialSortBy);
    const [sortOrder, setSortOrder] = useState<MetricSortOrder>('asc');

    useEffect(() => {
        setSortBy(initialSortBy);
        setSortOrder('asc');
    }, [initialSortBy]);

    const handleTableChange = useCallback(
        (_pagination: unknown, _filters: unknown, sorter: SorterResult<any> | SorterResult<any>[]) => {
            const single = Array.isArray(sorter) ? sorter[0] : sorter;
            if (!single?.order) {
                setSortBy(initialSortBy);
                setSortOrder('asc');
                return;
            }
            // Track sort by the column `key` (columnKey), not `field` (dataIndex).
            // getColumnSortOrder and the backend `sort_by` contract are keyed by the
            // column key; the label column intentionally has key !== dataIndex.
            setSortBy((single.columnKey ?? single.field) as string);
            setSortOrder(single.order === 'ascend' ? 'asc' : 'desc');
        },
        [initialSortBy],
    );

    const getColumnSortOrder = useCallback(
        (dataIndex: string): 'ascend' | 'descend' | null => {
            if (sortBy !== dataIndex) return null;
            return sortOrder === 'asc' ? 'ascend' : 'descend';
        },
        [sortBy, sortOrder],
    );

    const resetSort = useCallback(() => {
        setSortBy(initialSortBy);
        setSortOrder('asc');
    }, [initialSortBy]);

    return { sortBy, sortOrder, handleTableChange, getColumnSortOrder, resetSort };
}

export default useMetricSort;
